// Admin — gestion du lien public partageable de l'agenda (un seul lien global, révocable)
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, RefreshCw, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import api, { apiError } from "@/lib/api";
import { CATEGORIES_AGENDA } from "@/lib/constants";

export const OngletLienPublic = () => {
  const [config, setConfig] = useState(null);

  const charger = useCallback(async () => {
    try {
      const { data } = await api.get("/public-agenda-link");
      setConfig(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  const url = config ? `${window.location.origin}/public/agenda/${config.token}` : "";

  const regenerer = async () => {
    if (!window.confirm("Générer un nouveau lien ? L'ancien cessera immédiatement de fonctionner.")) return;
    try {
      const { data } = await api.post("/public-agenda-link/rotate");
      setConfig(data);
      toast.success("Nouveau lien généré — l'ancien est révoqué");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const basculer = async (actif) => {
    try {
      const { data } = await api.patch("/public-agenda-link", { actif });
      setConfig(data);
      toast.success(actif ? "Lien public activé" : "Lien public désactivé");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-5" data-testid="public-link-tab">
      <div className="bg-card rounded-2xl border border-border p-5 space-y-4 carte-douce">
        <div>
          <h3 className="font-serif text-xl font-semibold">Agenda public partageable</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Ce lien donne un accès en lecture seule aux événements marqués « visible publiquement ».
            Les notes internes ne sont jamais exposées.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <code className="flex-1 min-w-[260px] text-xs font-mono bg-muted rounded-xl px-4 py-3 break-all" data-testid="public-link-url">
            {url || "…"}
          </code>
          <Button
            variant="outline" className="rounded-full gap-1.5"
            onClick={() => { navigator.clipboard.writeText(url); toast.success("Lien copié"); }}
            data-testid="public-link-copy"
          >
            <Copy className="h-4 w-4" /> Copier
          </Button>
          <a href={url} target="_blank" rel="noopener noreferrer">
            <Button variant="ghost" className="rounded-full gap-1.5" data-testid="public-link-open">
              <ExternalLink className="h-4 w-4" /> Ouvrir
            </Button>
          </a>
        </div>
        <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t border-border/60">
          <div className="flex items-center gap-3">
            <Switch checked={Boolean(config?.actif)} onCheckedChange={basculer} data-testid="public-link-toggle" />
            <Label className="text-sm">Lien actif</Label>
          </div>
          <Button variant="outline" onClick={regenerer} className="rounded-full gap-1.5" data-testid="public-link-rotate">
            <RefreshCw className="h-4 w-4" /> Générer un nouveau lien
          </Button>
        </div>
      </div>

      <div className="bg-card rounded-2xl border border-border p-5 space-y-3 carte-douce">
        <div>
          <h3 className="font-serif text-xl font-semibold">Flux d'abonnement (.ics)</h3>
          <p className="text-sm text-muted-foreground mt-1">
            À coller dans Google Agenda (« Autres agendas → À partir de l'URL ») ou Apple Calendar
            (« Fichier → Nouvel abonnement »). Un flux global et un flux par catégorie.
          </p>
        </div>
        <div className="space-y-2">
          {[["", "Tout l'agenda"], ...Object.entries(CATEGORIES_AGENDA).map(([k, v]) => [k, v.label])].map(([cle, label]) => {
            const lien = config
              ? `${process.env.REACT_APP_BACKEND_URL}/api/public/agenda/${config.token}/calendrier.ics${cle ? `?categorie=${cle}` : ""}`
              : "";
            return (
              <div key={cle || "global"} className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium w-[150px] shrink-0">{label}</span>
                <code className="flex-1 min-w-[220px] text-[11px] font-mono bg-muted rounded-xl px-3 py-2 break-all" data-testid={`ics-url-${cle || "global"}`}>
                  {lien}
                </code>
                <Button
                  variant="ghost" size="sm" className="rounded-full gap-1.5"
                  onClick={() => { navigator.clipboard.writeText(lien); toast.success("Flux copié"); }}
                  data-testid={`ics-copy-${cle || "global"}`}
                >
                  <Copy className="h-3.5 w-3.5" /> Copier
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
